import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Section } from '../shared';

// Animated Workflow Diagram Component
const WorkflowDiagram = () => {
  const nodes = [
    { id: 'input', x: 50, y: 50, label: 'Audio Input' },
    { id: 'ai', x: 200, y: 100, label: 'AI Processing' },
    { id: 'enhance', x: 350, y: 50, label: 'Enhancement' },
    { id: 'output', x: 500, y: 100, label: 'Output' },
  ];

  const connections = [
    { from: 'input', to: 'ai' },
    { from: 'ai', to: 'enhance' },
    { from: 'enhance', to: 'output' },
    { from: 'ai', to: 'output' },
  ];

  return (
    <svg className="w-full h-full" viewBox="0 0 550 150" preserveAspectRatio="xMidYMid meet">
      {/* Background grid */}
      <defs>
        <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#FD802E" strokeWidth="0.5" strokeOpacity="0.1"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />

      {/* Animated connections */}
      {connections.map((conn, i) => {
        const fromNode = nodes.find(n => n.id === conn.from);
        const toNode = nodes.find(n => n.id === conn.to);
        return (
          <g key={i}>
            <motion.line
              x1={fromNode.x}
              y1={fromNode.y}
              x2={toNode.x}
              y2={toNode.y}
              stroke="#FD802E"
              strokeWidth="2"
              strokeOpacity="0.5"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1, delay: i * 0.3 }}
            />
            {/* Animated pulse along the line */}
            <motion.circle
              r="4"
              fill="#FD802E"
              initial={{ opacity: 0 }}
              animate={{
                opacity: [0, 1, 0],
                cx: [fromNode.x, toNode.x],
                cy: [fromNode.y, toNode.y],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.5,
                ease: "linear"
              }}
            />
          </g>
        );
      })}

      {/* Nodes */}
      {nodes.map((node, i) => (
        <motion.g key={node.id}>
          {/* Outer glow */}
          <motion.circle
            cx={node.x}
            cy={node.y}
            r="25"
            fill="none"
            stroke="#FD802E"
            strokeWidth="1"
            strokeOpacity="0.3"
            animate={{ 
              r: [25, 30, 25],
              strokeOpacity: [0.3, 0.5, 0.3]
            }}
            transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
          />
          {/* Main circle */}
          <motion.circle
            cx={node.x}
            cy={node.y}
            r="20"
            fill="#233D4C"
            stroke="#FD802E"
            strokeWidth="2"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.5 + i * 0.2 }}
          />
          {/* Icon placeholder */}
          <motion.circle
            cx={node.x}
            cy={node.y}
            r="8"
            fill="#FD802E"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3, delay: 0.7 + i * 0.2 }}
          />
          {/* Label */}
          <motion.text
            x={node.x}
            y={node.y + 40}
            textAnchor="middle"
            fill="#FD802E"
            fontSize="10"
            fontWeight="500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 + i * 0.1 }}
          >
            {node.label}
          </motion.text>
        </motion.g>
      ))}
    </svg>
  );
};

// Expandable Stats Card Component
const ExpandableCard = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const stats = [
    { value: '99%', label: 'Workflow Efficiency Boost' },
    { value: '10x', label: 'Faster Processing' },
    { value: '50M+', label: 'Audio Files Processed' },
    { value: '24/7', label: 'AI Availability' },
  ];

  return (
    <motion.div
      className="relative cursor-pointer mt-8"
      onHoverStart={() => setIsExpanded(true)}
      onHoverEnd={() => setIsExpanded(false)}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <motion.div
        className="bg-dark-base/50 rounded-2xl border border-neon-orange/20 overflow-hidden"
        animate={{
          borderColor: isExpanded ? 'rgba(253, 128, 46, 0.5)' : 'rgba(253, 128, 46, 0.2)',
          boxShadow: isExpanded ? '0 0 30px rgba(253, 128, 46, 0.2)' : '0 0 0px rgba(253, 128, 46, 0)',
        }}
      >
        {/* Header */}
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <motion.div
              className="w-10 h-10 rounded-lg bg-neon-orange/20 flex items-center justify-center"
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <svg className="w-5 h-5 text-neon-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </motion.div>
            <span className="text-white font-semibold">Learn More</span>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <svg className="w-5 h-5 text-neon-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </motion.div>
        </div>

        {/* Expandable Content */}
        <motion.div
          initial={false}
          animate={{ 
            height: isExpanded ? 'auto' : 0,
            opacity: isExpanded ? 1 : 0
          }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <div className="px-4 pb-4">
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, i) => (
                <motion.div
                  key={i}
                  className="bg-dark-deeper/50 rounded-xl p-4 border border-neon-orange/10"
                  initial={{ opacity: 0, y: 10 }}
                  animate={isExpanded ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="text-2xl font-display font-bold text-neon-orange">{stat.value}</div>
                  <div className="text-gray-400 text-sm">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

// Sequential Bullet Points Component
const BulletPoints = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const bullets = [
    "Advanced AI algorithms analyze and optimize your audio in real-time",
    "Seamless integration with all major DAWs and audio platforms",
    "Batch processing capabilities for handling thousands of files",
    "Custom workflow automation tailored to your specific needs",
    "Industry-leading noise reduction and audio enhancement",
  ];

  return (
    <ul ref={ref} className="space-y-4 mt-6">
      {bullets.map((bullet, i) => (
        <motion.li
          key={i}
          className="flex items-start gap-3"
          initial={{ opacity: 0, x: -20 }}
          animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
          transition={{ duration: 0.5, delay: i * 0.15 }}
        >
          <motion.div
            className="w-6 h-6 rounded-full bg-neon-orange/20 flex items-center justify-center flex-shrink-0 mt-0.5"
            initial={{ scale: 0 }}
            animate={isInView ? { scale: 1 } : { scale: 0 }}
            transition={{ duration: 0.3, delay: i * 0.15 + 0.2 }}
          >
            <svg className="w-3 h-3 text-neon-orange" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
            </svg>
          </motion.div>
          <span className="text-gray-400">{bullet}</span>
        </motion.li>
      ))}
    </ul>
  );
};

const AboutSection = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });
  
  // Parallax values
  const diagramY = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const textY = useTransform(scrollYProgress, [0, 1], [50, -50]);

  const stats = [
    { value: '10M+', label: 'Audio Files Processed' },
    { value: '50K+', label: 'Active Users' },
    { value: '99.9%', label: 'Uptime' },
    { value: '< 2s', label: 'Processing Time' },
  ];

  return (
    <Section id="about" className="bg-dark-deeper overflow-hidden">
      <div ref={containerRef} className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Side - Framed Image with Parallax */}
          <motion.div 
            className="relative order-2 lg:order-1"
            style={{ y: diagramY }}
          >
            <div className="w-full lg:w-full rounded-3xl p-2 lg:p-4 bg-transparent shadow-2xl">
              {/* Outer neon frame */}
              <div className="rounded-3xl p-1 bg-dark-deeper/20 border-2 border-neon-orange/80">
                {/* Inner card with subtle border - make image fill the area */}
                <div className="bg-dark-deeper/80 rounded-2xl p-2 lg:p-4 border border-neon-orange/10 ring-1 ring-neon-orange/10">
                  <img
                    src={"/aboutbg_result.webp"}
                    alt="About visual"
                    className="w-full h-[60vh] lg:h-[80vh] max-w-full rounded-xl object-cover block"
                  />
                </div>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-4 -left-4 w-full h-full bg-neon-orange/5 rounded-2xl -z-10 border border-neon-orange/10"></div>
            <motion.div
              className="absolute -bottom-8 -right-8 w-24 h-24 bg-neon-orange/10 rounded-full blur-2xl"
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
          </motion.div>
          
          {/* Right Side - Text Content with Parallax */}
          <motion.div 
            className="order-1 lg:order-2"
            style={{ y: textY }}
          >
            <motion.span 
              className="text-neon-orange font-semibold text-sm uppercase tracking-wider"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              About VeritoneX
            </motion.span>
            
            <motion.h2 
              className="text-3xl md:text-4xl font-heading font-bold text-white mt-4 mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              Creating Smarter Audio Workflows{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-orange to-neon-orange-light">
                for the Future of Sound.
              </span>
            </motion.h2>
            
            <motion.p 
              className="text-gray-400 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              VeritoneX redefines modern audio workflow automation for creators and teams. Built with intelligent technology and seamless design. We help produce, organize, and optimize audio production faster and smarter than ever before today.
            </motion.p>
            
            {/* Sequential Bullet Points */}
            <BulletPoints />
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4 mt-8">
              {stats.map((stat, i) => (
                <motion.div
                  key={i}
                  className="bg-dark-base/50 rounded-xl p-4 border border-neon-orange/10"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  whileHover={{ 
                    borderColor: 'rgba(253, 128, 46, 0.3)',
                    boxShadow: '0 5px 20px rgba(253, 128, 46, 0.1)'
                  }}
                >
                  <div className="text-2xl md:text-3xl font-display font-bold text-neon-orange">{stat.value}</div>
                  <div className="text-gray-500 text-sm">{stat.label}</div>
                </motion.div>
              ))}
            </div>
            
            {/* Expandable Learn More Card */}
            <ExpandableCard />
          </motion.div>
        </div>
      </div>
    </Section>
  );
};

export default AboutSection;
